/**
 * Map D-Tools revise / staging lines into VO scope lines for diff + composition.
 */

import type { DtoolsReviseLine } from "./dtoolsProposalRevise";
import type { VoScopeLine } from "./variationOrderDiff";
import { placementFromDtoolsLine } from "./variationOrderPlacement";
import {
  coalesceNonEmpty,
  presentationFieldsFromReviseLine,
} from "./variationOrderCataloguePresentation";
import { roundVoMoney } from "./variationOrderTypes";

/** Stable identity for VO diff — prefer D-Tools item id, then persisted proposal item id, then revise key. */
export function stableLineKeyFromDtoolsReviseLine(line: DtoolsReviseLine): string {
  const dtoolsId = String(line.dtoolsItemId ?? "").trim();
  if (dtoolsId) return `dtools:${dtoolsId}`;
  if (line.persistedItemId && line.persistedItemId > 0) return `item:${line.persistedItemId}`;
  const key = String(line.key ?? "").trim();
  if (key) return `key:${key}`;
  return `tmp:${String(line.id ?? "").trim() || Math.random().toString(36).slice(2)}`;
}

function parentStableLineKeyFromReviseLines(
  line: DtoolsReviseLine,
  byId: Map<string, DtoolsReviseLine>,
  byDtoolsId: Map<string, DtoolsReviseLine>,
): string | null {
  const rawParent = String(line.parentId ?? "").trim();
  if (!rawParent) return null;
  const parent =
    byId.get(rawParent) ??
    byDtoolsId.get(rawParent) ??
    null;
  if (parent) return stableLineKeyFromDtoolsReviseLine(parent);
  // Parent may already be a stable key or dtools id
  if (rawParent.startsWith("dtools:") || rawParent.startsWith("item:") || rawParent.startsWith("key:")) {
    return rawParent;
  }
  return `dtools:${rawParent}`;
}

export function dtoolsReviseLinesToVoScopeLines(lines: DtoolsReviseLine[]): VoScopeLine[] {
  const byId = new Map<string, DtoolsReviseLine>();
  const byDtoolsId = new Map<string, DtoolsReviseLine>();
  for (const line of lines) {
    const id = String(line.id ?? "").trim();
    if (id) byId.set(id, line);
    const dtoolsId = String(line.dtoolsItemId ?? "").trim();
    if (dtoolsId) byDtoolsId.set(dtoolsId, line);
  }

  return lines.map((line, idx) => {
    const quantity = Math.max(0, Number(line.quantity) || 0);
    const unitPrice = Number(line.unitPrice) || 0;
    const lineTotal =
      line.lineTotal != null && Number.isFinite(Number(line.lineTotal))
        ? roundVoMoney(Number(line.lineTotal))
        : roundVoMoney(unitPrice * quantity);
    const presentation = presentationFieldsFromReviseLine(line);
    const name =
      coalesceNonEmpty(
        line.displayName,
        (line as { productName?: string | null }).productName,
        presentation.displayName,
        [presentation.brand, presentation.model].filter(Boolean).join(" "),
        presentation.model,
      ) || "Product";
    const placement = placementFromDtoolsLine({
      systemId: line.systemId,
      systemName: line.systemName,
      locationId: line.locationId,
      roomName: line.roomName,
      locationName: (line as { locationName?: string | null }).locationName,
    });
    const parentStableLineKey = parentStableLineKeyFromReviseLines(line, byId, byDtoolsId);
    return {
      stableLineKey: stableLineKeyFromDtoolsReviseLine(line),
      name,
      brand: presentation.brand,
      model: presentation.model,
      description: presentation.description,
      displayName: presentation.displayName,
      imageUrl: presentation.imageUrl,
      catalogProductId: presentation.catalogProductId ?? null,
      projectCatalogueId: presentation.projectCatalogueId ?? null,
      catalogueKey: presentation.catalogueKey ?? null,
      customerExplanation: presentation.customerExplanation ?? null,
      systemKey: placement.systemKey,
      roomKey: placement.roomKey,
      systemId: placement.systemId,
      systemName: placement.systemName,
      roomId: placement.roomId,
      roomName: placement.roomName,
      parentStableLineKey,
      isAccessory: Boolean(line.isAccessory || parentStableLineKey),
      quantity,
      unitPrice: roundVoMoney(unitPrice),
      lineTotal,
      sourceProposalItemId: line.persistedItemId && line.persistedItemId > 0 ? line.persistedItemId : null,
      sortOrder: idx,
    };
  });
}
